import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Paperclip, Send, X } from 'lucide-react';
import { VStack, HStack } from '@/components/core';
import { useTranslation } from 'react-i18next';
import { useSupportChat } from '@/hooks/useSupportChat';

interface SupportChatPanelProps {
  onClose: () => void;
}

export function SupportChatPanel({ onClose }: SupportChatPanelProps): ReactNode {
  const { t } = useTranslation('common');
  const {
    ticketId,
    tickets,
    messages,
    inputValue,
    setInputValue,
    file,
    setFile,
    loading,
    guestEmail,
    setGuestEmail,
    guestName,
    setGuestName,
    guestValidated,
    isGuest,
    selectTicket,
    handleSend,
    initGuestTicket,
    scrollRef,
  } = useSupportChat();

  return (
    <div className="fixed right-token-md top-20 md:top-auto md:bottom-20 bg-background/95 backdrop-blur-md rounded-token-button border border-border shadow-token-modal z-50 w-[320px] overflow-hidden">
      <HStack align="center" justify="between" className="px-token-sm py-token-sm border-b border-border">
        <div className="text-sm font-semibold">{t('support.title')}</div>
        <Button size="icon" variant="ghost" onClick={onClose} aria-label={t('support.closeChat')}>
          <X className="h-4 w-4" />
        </Button>
      </HStack>

      {(isGuest && !guestValidated) ? (
        <VStack gap="sm" className="px-token-sm py-token-sm">
          <div className="text-xs text-muted-foreground">{t('support.guestEmailDescription')}</div>
          <input
            className="w-full bg-transparent border border-border rounded-token-sm px-token-sm py-token-sm text-sm outline-none focus:border-primary"
            placeholder={t('support.emailPlaceholder')}
            aria-label={t('support.emailLabel')}
            value={guestEmail}
            onChange={(e) => setGuestEmail(e.target.value)}
            type="email"
            inputMode="email"
          />
          <input
            className="w-full bg-transparent border border-border rounded-token-sm px-token-sm py-token-sm text-sm outline-none focus:border-primary"
            placeholder={t('support.namePlaceholder')}
            aria-label={t('support.nameLabel')}
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
          />
          <Button className="w-full" disabled={loading || !guestEmail} onClick={initGuestTicket}>
            {t('support.startChatButton')}
          </Button>
          <div className="text-[10px] text-muted-foreground">{t('support.emailNote')}</div>
        </VStack>
      ) : (
        <VStack gap="sm" className="max-h-80 overflow-auto px-token-sm py-token-sm" ref={scrollRef}>
          {!isGuest && tickets.length > 0 && (
            <div className="mb-token-sm text-[11px] text-muted-foreground">
              {t('support.switchTicket')}
              <div className="mt-token-xs flex flex-wrap gap-token-xs">
                {tickets.map((ticket) => (
                  <Button
                    key={ticket.id}
                    variant="ghost"
                    size="sm"
                    onClick={() => selectTicket(ticket.id)}
                    className={`h-auto px-token-sm py-[2px] text-[11px] ${ticket.id === ticketId ? 'border-primary text-primary' : 'border-border text-foreground hover:bg-muted/50'}`}
                  >
                    #{ticket.id}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="text-xs text-muted-foreground">{t('support.emptyStateHint')}</div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`text-sm p-token-sm rounded-token-sm ${m.is_from_admin ? 'bg-muted/50' : 'bg-primary/10'}`}>
                {m.content}
                {m.attachment_url && (
                  <div className="mt-token-xs text-xs underline">
                    <a href={m.attachment_url} target="_blank" rel="noreferrer">{t('support.openAttachment')}</a>
                  </div>
                )}
                <div className="mt-token-xs text-[10px] text-muted-foreground">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</div>
              </div>
            ))
          )}
        </VStack>
      )}

      <div className="p-token-sm border-t border-border">
        {(isGuest && !guestValidated) ? (
          <div className="text-[10px] text-muted-foreground">{t('support.guestHint')}</div>
        ) : (
          <>
            <HStack align="center" gap="sm">
              <label className="inline-flex items-center justify-center h-9 w-9 rounded-token-sm hover:bg-muted cursor-pointer" aria-label={t('support.attachFile')}>
                <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label={t('support.attachFile')} />
                <Paperclip className={`h-4 w-4 ${file ? 'text-primary' : ''}`} />
              </label>
              <input
                className="flex-1 bg-transparent border border-border rounded-token-sm px-token-sm py-token-sm text-sm outline-none focus:border-primary"
                placeholder={file ? `${t('support.messagePlaceholder')} (${t('support.file')}: ${file.name})` : t('support.messagePlaceholder')}
                aria-label={t('support.messageLabel')}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleSend(); } }}
              />
              <Button size="icon" disabled={loading || (!inputValue.trim() && !file)} onClick={handleSend} aria-label={t('buttons.send')}>
                <Send className="h-4 w-4" />
              </Button>
            </HStack>
            <div className="mt-token-xs text-[10px] text-muted-foreground">
              {t('support.ticketInfo', { ticketId: ticketId ? `#${ticketId}` : t('support.ticketCreatedOnSend') })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
